// Akku consent wiring.
//
// WHY THIS FILE EXISTS, rather than the one-line script-tag install:
//
// The install snippet auto-initialises from `data-akku-site-key` and prompts immediately, which put
// the consent dialog on top of the LOGIN form — a visitor had to answer a privacy question before
// they could sign in. So the site key is deliberately NOT on the script tag (that attribute is what
// triggers auto-init) and we drive init() ourselves.
//
// AND WHY INIT IS DEFERRED UNTIL SIGN-IN, not merely the prompt:
//
// `deferPrompt` alone suppresses the banner but still mounts the SDK's floating "Manage preferences"
// control for anyone who ALREADY has a decision on file — deliberately, because withdrawal has to
// stay as easy as giving it. That control appeared on the login page, which is not wanted here. The
// SDK offers no way to mount it later or retract it, so the only lever is to not initialise until
// there is a signed-in person to show it to.
//
// This is safe, and specifically it does NOT create a gap where tags run unconsented:
//
//   - the synchronous <head> pre-gate (akku-head) runs independently of init() and gates from the
//     cached decision;
//   - gated elements are `type="text/plain"` / `data-src`, so they NEVER self-execute — a denied tag
//     cannot run whether or not the SDK has initialised;
//   - this app currently marks no tags with `data-akku-category` at all, so nothing is waiting on it.
//
// If that last point changes — if a tag on the LOGIN page is ever gated by category — revisit this:
// a `required` category's tag would stay inert until sign-in, because only the async SDK knows which
// categories are required. Prefer initialising earlier then, and solve the control's visibility
// another way.

import { retrieveData } from '../storage/Storage';
import store from '../redux/store';
import keycloak from '../hooks/keycloak';

const SITE_KEY = process.env.REACT_APP_AKKU_SITE_KEY;
const API_HOST = process.env.REACT_APP_AKKU_API_HOST;

/** Dispatched by src/storage/Storage.ts whenever `authToken` is written or cleared. */
export const AUTH_CHANGED_EVENT = 'allaboard:auth-changed';

/** The SDK's own storage key. See hasRecordedDecision() for why this is read directly. */
const SDK_STORAGE_KEY = 'akku_consent';

/** The async CDN script may not have executed by the time our bundle runs. */
const WAIT_TIMEOUT_MS = 10_000;
const WAIT_INTERVAL_MS = 50;

let initPromise: Promise<void> | null = null;

function waitForSdk(): Promise<NonNullable<Window['AkkuConsent']>> {
  return new Promise((resolve, reject) => {
    if (window.AkkuConsent) return resolve(window.AkkuConsent);
    // Polling rather than a `load` listener: the tag is `async`, so it may already have finished by
    // the time we get here, in which case that event has been and gone.
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      if (window.AkkuConsent) {
        window.clearInterval(timer);
        resolve(window.AkkuConsent);
      } else if (Date.now() - startedAt > WAIT_TIMEOUT_MS) {
        window.clearInterval(timer);
        reject(new Error('Akku SDK did not load'));
      }
    }, WAIT_INTERVAL_MS);
  });
}

/**
 * Whether this browser already has a consent decision on file.
 *
 * Read from the SDK's storage key rather than its API, because the API cannot answer the question:
 * `getConsent()` merges every REQUIRED category in as `true`, so someone never asked is
 * indistinguishable from someone who accepted, on any site that has a required category. The SDK
 * computes exactly this internally (`wouldPrompt`) but does not expose it — the real gap, worth
 * raising upstream. Until then this reads the same key the SDK itself reads.
 */
function hasRecordedDecision(): boolean {
  try {
    return window.localStorage.getItem(SDK_STORAGE_KEY) !== null;
  } catch {
    // Storage denied (private mode, blocked cookies). Treat as undecided — the cost is asking again,
    // which is the safe direction. Never silently assume consent.
    return false;
  }
}

function isSignedIn(): boolean {
  try {
    // Deliberately decrypts rather than checking the key's presence: logout writes an EMPTY string,
    // and an empty string still encrypts to a non-empty stored value — so `getItem() !== null` would
    // report a logged-out user as signed in. retrieveData returns the decrypted '', which is falsy.
    return Boolean(retrieveData('authToken', true));
  } catch {
    return false;
  }
}

/**
 * Removes the SDK's floating control.
 *
 * Needed on sign-out: the SDK is already initialised for this page's lifetime, and logout is SPA
 * navigation rather than a reload, so without this the control stays on screen over the login form —
 * the exact thing this file exists to prevent.
 *
 * Reaching into the shadow root because the SDK exports no way to retract the control once mounted
 * (`removeFallback` is internal). That is a second upstream gap; this is the containment for it.
 */
function hideWayBackControl(): void {
  const host = document.getElementById('akku-consent-host');
  const root: ParentNode | null = host?.shadowRoot ?? host;
  root?.querySelector('.akku-fab')?.remove();
}

/**
 * The signed-in person's display name and email, for the console's Subject column.
 *
 * Resolved with the SAME precedence and from the same state as the app's own header
 * (`src/components/Header/Header.tsx:20-21`), so the console shows exactly what the user sees next to
 * their avatar — anything else would be confusing to reconcile during a support call.
 *
 * Two attributes only. Every one is personal data that lands in a consent record and has to be
 * erasable on request, so the set stays as small as the purpose needs.
 *
 * Read from the store directly rather than via useSelector because this is not a component. Called
 * from attachSubjectAttributes(), which polls it through a store subscription until the values exist
 * and then unsubscribes — so it runs a handful of times per sign-in, not on every render.
 */
function subjectAttributes(): Array<{ key: string; value: string }> {
  const state = store.getState() as any;

  // PRIMARY source: the encrypted session storage the login writes SYNCHRONOUSLY. All three login
  // paths do `storeData('loginUserData', response.data.data, true)` at the moment they sign in
  // (KeycloakAuthHandler.tsx:44, GoogleLogin.tsx:41, LoginForm.tsx:81) — the exact same object the
  // header renders name/email from, but available immediately and not subject to redux-timing. This
  // is what the user pointed at: read it the way Header.tsx would from storage, so a consent write
  // that happens right after sign-in still has the identity rather than racing an async redux fetch.
  let storedLogin: any = {};
  try {
    storedLogin = retrieveData('loginUserData', true) ?? {};
  } catch {
    storedLogin = {};
  }

  // Redux carries the same data once populated; kept as a fallback alongside storage.
  const loginUserData = storedLogin?.name || storedLogin?.email
    ? storedLogin
    : state?.loginUserData?.userData?.data;
  const employeeDetails = state?.employee?.employeeDetails;

  // The Keycloak token is the AUTHORITATIVE source for an SSO login and the reason that path is
  // covered. `keycloak.tokenParsed` is the identity provider's own signed assertion, populated the
  // instant auth completes — before the app's user/employee fetch resolves, and regardless of whether
  // the backend HR record carries a display name. It is read here directly, per the instruction to
  // take the user details from the Keycloak widget rather than depend on the redux round-trip.
  //
  // Every common name claim is checked, because realms differ in which they emit: `name`, the
  // `given_name`/`family_name` pair, then `preferred_username` (Keycloak always sets it; it is the
  // login/username and a sane last resort). `email` likewise falls back to `preferred_username` only
  // if it looks like an address.
  const tokenParsed: any = keycloak?.tokenParsed ?? {};
  const preferred: string = tokenParsed.preferred_username ?? '';
  const tokenName: string =
    tokenParsed.name ||
    `${tokenParsed.given_name ?? ''} ${tokenParsed.family_name ?? ''}`.trim() ||
    preferred;
  const tokenEmail: string =
    tokenParsed.email || (preferred.includes('@') ? preferred : '');

  // Redux first (matches the header for a normally-onboarded employee), token as the authoritative
  // fallback that makes SSO work even when the redux fetch is empty or slow.
  const name: string =
    loginUserData?.name ||
    `${employeeDetails?.firstName ?? ''} ${employeeDetails?.lastName ?? ''}`.trim() ||
    tokenName;
  const email: string =
    loginUserData?.email || employeeDetails?.officialEmail || tokenEmail;

  const attributes: Array<{ key: string; value: string }> = [];
  if (name) attributes.push({ key: 'user_name', value: name });
  if (email) attributes.push({ key: 'email', value: email });

  // Diagnostic: the SSO token's actual claims, logged ONCE, so a login on a new realm reveals exactly
  // what identity is available instead of us guessing. Keys and values both — this is a self-hosted
  // admin tool and whoever reads this console is already authorised to see the signed-in user.
  if (!subjectAttributesLogged && Object.keys(tokenParsed).length > 0) {
    subjectAttributesLogged = true;
    // eslint-disable-next-line no-console
    console.info('[Akku consent] keycloak token claims:', {
      name: tokenParsed.name,
      given_name: tokenParsed.given_name,
      family_name: tokenParsed.family_name,
      preferred_username: tokenParsed.preferred_username,
      email: tokenParsed.email,
      resolved: { name, email },
    });
  }

  return attributes;
}

/** One-shot guard so the token-claims diagnostic prints once per page life, not on every poll. */
let subjectAttributesLogged = false;

let unsubscribeStore: (() => void) | null = null;

/**
 * Attaches the attributes, and KEEPS WATCHING until they exist.
 *
 * The subscription is the whole point, and its absence was a real bug: `authToken` is written at
 * LoginForm.tsx:120, which is what tells us someone signed in — but the name and email only reach the
 * store at line 169, after an async fetch of the employee record. Reading the store once, at sign-in,
 * therefore found nothing every time, and an early `return` on the empty array made it fail silently:
 * consent recorded, Subject column still "—", nothing in the console to explain why.
 *
 * So this sets whatever is available now (possibly nothing) and subscribes, re-setting as soon as the
 * values appear, then unsubscribes. Attributes attach to writes made AFTER the call, so a visitor who
 * answers the banner in the first few hundred milliseconds — before the employee fetch resolves — can
 * still produce one event without them. Living with that rather than delaying the banner on a network
 * call: blocking the consent prompt on an unrelated request is the worse trade.
 *
 * The `typeof` check is load-bearing too, not defensive padding: `setSubjectAttributes` arrived in
 * AK-8457, and a CDN still serving an earlier bundle would make an unconditional call throw inside the
 * sign-in path and take consent capture down with it.
 */
function attachSubjectAttributes(): void {
  const sdk = window.AkkuConsent;
  if (!sdk || typeof sdk.setSubjectAttributes !== 'function') return;

  const apply = (): boolean => {
    const attributes = subjectAttributes();
    if (attributes.length === 0) return false;
    sdk.setSubjectAttributes!(attributes);
    // Deliberately logged. This whole path failed silently for an hour of debugging — consent
    // recorded, Subject column blank, nothing to explain why (see this function's own doc comment).
    // A one-line breadcrumb turns "is it even sending attributes?" from a guessing game into a glance
    // at the console. Keys only in a normal build; values shown because this is a self-hosted admin
    // tool and the operator debugging it is already authorised to see the signed-in user.
    // eslint-disable-next-line no-console
    console.info('[Akku consent] subject attributes attached:', attributes);
    return true;
  };

  unsubscribeStore?.();
  unsubscribeStore = null;

  if (apply()) return; // already in the store — a reload with a warm session
  // Not yet available. Subscribe and keep trying. If it NEVER resolves, that is itself the signal —
  // logged once after a short grace period so a permanently-empty identity is visible rather than an
  // eternal silent wait.
  let settled = false;
  unsubscribeStore = store.subscribe(() => {
    if (apply()) {
      settled = true;
      unsubscribeStore?.();
      unsubscribeStore = null;
    }
  });
  window.setTimeout(() => {
    if (!settled) {
      // eslint-disable-next-line no-console
      console.warn(
        '[Akku consent] no subject attributes resolved after sign-in — the consent event will ' +
          'have an empty Subject column. Checked loginUserData.userData.data, employee.employeeDetails, ' +
          'and keycloak.tokenParsed; all lacked a name/email.',
      );
    }
  }, 4000);
}

function startIfSignedIn(): void {
  if (!isSignedIn()) {
    // Signed out. If the SDK was initialised earlier in this page's life, take its control away.
    if (initPromise) {
      hideWayBackControl();
      // Stop watching the store as well as clearing the values: a pending subscription would attach
      // the NEXT person's details while the SDK still holds the previous session's context.
      unsubscribeStore?.();
      unsubscribeStore = null;
      // Stop labelling writes with the person who just left. A preference change made from the
      // login screen would otherwise be attributed to them.
      window.AkkuConsent?.setSubjectAttributes?.([]);
    }
    return;
  }
  void start();
}

function start(): Promise<void> {
  if (initPromise) return initPromise;

  if (!SITE_KEY) {
    // No Akku site configured for this build. Not an error — only some environments have one. Logged
    // so an integrator can tell this apart from a failed script load.
    console.info('Akku consent: REACT_APP_AKKU_SITE_KEY not set for this build — consent disabled');
    initPromise = Promise.resolve();
    return initPromise;
  }

  initPromise = waitForSdk()
    .then((sdk) =>
      sdk.init({
        siteKey: SITE_KEY,
        ...(API_HOST ? { apiHost: API_HOST } : {}),
        // Still deferred even though we only get here once signed in: init() must not race ahead of
        // the decision below about whether this person has already been asked.
        deferPrompt: true,
      }),
    )
    .then(() => {
      // BEFORE the banner: attributes are attached to writes made after this call, never
      // retroactively, so setting them after showBanner() would lose the race with a fast click.
      attachSubjectAttributes();
      if (!hasRecordedDecision()) window.AkkuConsent?.showBanner();
    })
    .catch((err: unknown) => {
      // A broken consent install degrades to "no consent UI", never to a broken host page. Nothing
      // here may throw into the app's startup path.
      console.error('Akku consent: initialisation failed', err);
    });

  return initPromise;
}

/**
 * Wires consent to the app's session. Safe to call more than once.
 *
 * Nothing Akku-related renders until someone is signed in — no banner, no floating control.
 */
export function initAkkuConsent(): void {
  window.addEventListener(AUTH_CHANGED_EVENT, startIfSignedIn);
  // Already signed in on this load (a returning session)?
  startIfSignedIn();
}
